import {
  useDeleteFilesMutation,
  useUploadFileMutation,
} from '@gen2/api/files/hooks';
import { FollowUpKeys } from '@gen2/api/follow-up/hooks';
import { TInviteRequestFile } from '@gen2/api/invite-requests/api';
import { InviteRequestsKeys } from '@gen2/api/invite-requests/hooks';
import { useSaveLiveFormMutation } from '@gen2/api/live-form/hooks';
import { TemplateKeys } from '@gen2/api/templates/hooks';
import { useConfirmationModalStore } from '@gen2/app/components/confirmation-modal/hooks/confirmation-modal-store';
import { useFileStore } from '@gen2/app/components/invite-request/hooks/file-store';
import { SetupPlugin } from '@gen2/app/components/rich-text-editor/plugins/SetupPlugin/SetupPlugin';
import RichTextEditor from '@gen2/app/components/rich-text-editor/rich-text-editor';
import { TInviteRequestsForm } from '@gen2/app/invites/send-invites/requests/requests';
import { MAX_MESSAGE_LENGTH } from '@gen2/app/invites/send-invites/requests/schema';
import { LiveFormType } from '@gen2/app/invites/send-invites/store';
import { queryClient } from '@gen2/config';
import { useAuth, useRouter, useToast } from '@gen2/hooks';
import { $generateHtmlFromNodes } from '@lexical/html';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { Alert, FormHelperText, Stack } from '@mui/material';
import { DragAndDrop, formatSize, renderIcon } from '@nx-fe/components';
import { $getRoot, EditorState, LexicalEditor } from 'lexical';
import React, { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useFormModalStore } from '../../hooks/modal-store';
import { TRequestContainer, useRequestHook } from '../../hooks/request';
import { RequestContacts } from '../../request-contact/request-contact';
import {
  StyledAttachments,
  StyledAttachmentsLabel,
  StyledFileUploadRequest,
} from '../file-upload/file-upload.styled';
import { formValidationSchema } from '../file-upload/schema';
import { CreateFormModal } from './create-form-modal';
import LiveFormControls from './live-form-controls';
import { TemplateCreateFormModal } from './template-create-form-modal';

export type TFileUploadRequestForm = {
  rawMessage?: string;
  message: string;
  meta: string;
};

export type TLiveFormRequestProps = {
  id: number;
  container?: TRequestContainer;
};

export const LiveFormRequest = React.memo(
  ({ id, container = 'invite' }: TLiveFormRequestProps) => {
    const {
      setValue,
      formState: { errors },
    } = useFormContext<TInviteRequestsForm>();
    const { onUpdate, req } = useRequestHook({ id, container });

    const [isUploadLoading, setIsUploadLoading] = useState(false);
    const { mutate: fileUploadMutation } = useUploadFileMutation();
    const { mutate: saveMutate } = useSaveLiveFormMutation();
    const { isLoading: isFileDeleting } = useDeleteFilesMutation();
    const { setIsConfirmationModalOpen } = useConfirmationModalStore();
    const { setFile } = useFileStore();
    const [fileErrors, setFileErrors] = useState<string[]>();
    const toast = useToast();
    const { featureFlags } = useAuth();
    const router = useRouter();
    const isInTemplates = router.pathname.includes('templates');

    const {
      fileId,
      fileUrl,
      isCreateFormModalOpen,
      isNutrient,
      handleOnSelected,
      liveFormType: liveFormTypeFromStore,
      openCreateFormModal,
      openTemplateCreateFormModal,
      closeCreateFormModal,
      setLiveFormType,
      setOpenViewer,
    } = useFormModalStore();

    const { t } = useTranslation('sendInvite');

    const onMessageChange = (
      editorState: EditorState,
      editor: LexicalEditor,
    ) => {
      editorState.read(() => {
        const root = $getRoot();

        const rawText = root.getTextContent();
        const html = $generateHtmlFromNodes(editor);

        if (rawText.length === 0) {
          setValue(`requests.${id}.meta`, '');
          setValue(`requests.${id}.description`, '');
        } else {
          setValue(`requests.${id}.meta`, JSON.stringify(editorState.toJSON()));
          setValue(`requests.${id}.description`, html);
          setValue(`requests.${id}.descriptionText`, rawText);
        }
      });
    };

    const onEditorSetup = (editor: LexicalEditor) => {
      const state = editor.getEditorState();

      state.read(() => {
        const rawHTML = $generateHtmlFromNodes(editor);

        // Only set description if it's not empty
        if (
          rawHTML === '<p><br></p>' ||
          rawHTML === '<p></p>' ||
          rawHTML === '<br>' ||
          !rawHTML.trim()
        ) {
          setValue(`requests.${id}.description`, '');
        } else {
          setValue(`requests.${id}.description`, rawHTML);
        }
      });
    };

    const updateLiveFormType = useCallback(
      async (liveFormType: LiveFormType, currentFileId?: string) => {
        const idToUse = currentFileId || fileId;
        if (!idToUse) {
          return;
        }

        const payload = {
          fileId: idToUse,
          liveFormData: {
            xfdf_data: '',
            json_data: JSON.stringify({
              format: 'https://pspdfkit.com/instant-json/v1',
              formFieldValues: [],
              annotations: [],
              formFields: [],
              customFromAPI: true,
            }),
            type: liveFormType,
            is_type_signature_enabled: true,
          },
        };

        saveMutate(payload, {
          onSuccess: () => {
            setLiveFormType(liveFormType);
          },
          onError: () => {
            toast.show({
              text: 'Failed to save form',
              variant: 'error',
            });
          },
        });
      },
      [saveMutate, fileId, toast, setLiveFormType],
    );

    const onFileUpload = async (files: File[]) => {
      if (req?.id === undefined) return;

      setIsUploadLoading(true);

      // should not accept multiple files
      if (files.length > 1) {
        toast.show({
          text: t('request.fileUpload.message.onlyOneFile') ?? '',
          variant: 'error',
        });
        setIsUploadLoading(false);
        return;
      }

      const file = files[0];

      try {
        await formValidationSchema.validate({ file }, { abortEarly: false });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        setFileErrors(err.errors);
        setIsUploadLoading(false);
        return;
      }

      fileUploadMutation(
        {
          type: 'request',
          typeId: req.id,
          file,
        },
        {
          onSuccess: (response) => {
            const fileData = (
              response as unknown as {
                data: { data: { file: TInviteRequestFile } };
              }
            ).data.data.file;

            if (container === 'invite') {
              queryClient.invalidateQueries({
                queryKey: [InviteRequestsKeys.getInviteRequests],
              });
              queryClient.invalidateQueries({
                queryKey: [InviteRequestsKeys.getInviteRequestGroups],
              });
            }

            if (container === 'follow-up') {
              queryClient.invalidateQueries({
                queryKey: [FollowUpKeys.getFollowUp],
              });
              queryClient.invalidateQueries({
                queryKey: [FollowUpKeys.getFollowUps],
              });
            }

            if (container === 'template') {
              queryClient.invalidateQueries({
                queryKey: [TemplateKeys.getTemplateRequests],
              });
              queryClient.invalidateQueries({
                queryKey: [TemplateKeys.getTemplateRequestGroups],
              });
            }

            setLiveFormType(undefined);
            closeCreateFormModal();

            if (isInTemplates) {
              openTemplateCreateFormModal(
                fileData.id,
                isNutrient,
                async (liveFormType: LiveFormType) => {
                  setLiveFormType(liveFormType);

                  await updateLiveFormType(liveFormType, fileData.id);

                  closeCreateFormModal();
                  setOpenViewer(fileData.id, isNutrient, liveFormType);
                },
              );
            } else {
              openCreateFormModal(
                fileData.id,
                isNutrient,
                async (liveFormType: LiveFormType) => {
                  setLiveFormType(liveFormType);

                  await updateLiveFormType(liveFormType, fileData.id);

                  closeCreateFormModal();
                  setOpenViewer(fileData.id, isNutrient, liveFormType);
                },
              );
            }
          },
          onError: (_err: unknown) => {
            toast.show({
              text: t('request.fileUpload.message.error') ?? '',
              variant: 'error',
            });
          },
          onSettled: () => setIsUploadLoading(false),
        },
      );
    };

    const clearError = (errorMessage: string) => {
      setFileErrors((prev) => prev?.filter((err) => err !== errorMessage));
    };

    const handleDelete = (file: TInviteRequestFile) => {
      if (isFileDeleting) return;

      setFile({
        ...file,
        request: req,
      });

      setIsConfirmationModalOpen(true);
    };

    const formTypeErrorDescription: string = useMemo(() => {
      if (fileErrors && fileErrors?.includes('request.liveForm.file.type')) {
        return t('request.liveForm.file.type');
      }

      return '';
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileErrors]);

    const contacts = useMemo(() => {
      if (req.contacts) {
        return req.contacts;
      }

      return [];
    }, [req]);

    const attachments = useMemo(() => {
      if (!req?.files) {
        return [];
      }

      return req.files.filter((file) => file.type === 'send_invite_attachment');
    }, [req]);

    const isThisRequestsModal = useMemo(() => {
      if (!fileId) return false;
      return attachments.some((f) => f.id === fileId);
    }, [attachments, fileId]);

    const handleOnCloseCreateFormModal = useCallback(async () => {
      if (!liveFormTypeFromStore) {
        await updateLiveFormType('individual', fileId);
      }

      closeCreateFormModal();
    }, [
      closeCreateFormModal,
      fileId,
      liveFormTypeFromStore,
      updateLiveFormType,
    ]);

    return (
      <StyledFileUploadRequest key={req.id}>
        {(container === 'invite' || container === 'follow-up') && (
          <RequestContacts request={req} contacts={contacts} />
        )}

        <div>
          <RichTextEditor
            onBlur={onUpdate}
            placeholder={t('request.liveForm.message.placeholder') ?? ''}
          >
            <OnChangePlugin onChange={onMessageChange} />
            <SetupPlugin
              onSetup={onEditorSetup}
              maxLength={MAX_MESSAGE_LENGTH}
              defaultHtmlState={req.description}
              defaultEditorState={req.meta}
            />
          </RichTextEditor>
          {(errors?.requests && errors.requests[id])?.descriptionText
            ?.message && (
            <FormHelperText error>
              {t(errors.requests[id]?.descriptionText?.message ?? '')}
            </FormHelperText>
          )}
        </div>
        {!!attachments.length && (
          <div>
            <StyledAttachmentsLabel>
              <span>Form</span>
            </StyledAttachmentsLabel>
            <StyledAttachments>
              {attachments.map((file) => (
                <LiveFormControls
                  key={file.id}
                  progress={100}
                  fileId={file.id}
                  fileName={file.original_name}
                  fileSize={`(${formatSize(file.size)})`}
                  isUploading={false}
                  icon={renderIcon(file.mime_type)}
                  onDelete={() => handleDelete(file)}
                  data-cy={`file-${file.id}`}
                  contacts={contacts}
                />
              ))}
            </StyledAttachments>
          </div>
        )}
        <Stack rowGap={2}>
          {!!fileErrors?.length &&
            fileErrors.map((err) => {
              if (err === 'request.liveForm.file.type') {
                return null;
              }

              return (
                <Alert
                  key={err}
                  severity="error"
                  onClose={() => clearError(err)}
                >
                  {t(err)}
                </Alert>
              );
            })}
          {!attachments.length && (
            <DragAndDrop
              isLoading={isUploadLoading}
              onUpload={onFileUpload}
              accept=".pdf"
              description={formTypeErrorDescription}
              multiple={false}
            />
          )}
        </Stack>

        {isCreateFormModalOpen && isThisRequestsModal && isInTemplates && (
          <TemplateCreateFormModal
            fileId={fileId ?? ''}
            fileUrl={fileUrl ?? ''}
            open={isCreateFormModalOpen}
            onClose={handleOnCloseCreateFormModal}
            onSelected={handleOnSelected}
          />
        )}
        {isCreateFormModalOpen && isThisRequestsModal && !isInTemplates && (
          <CreateFormModal
            fileId={fileId ?? ''}
            fileUrl={fileUrl ?? ''}
            open={isCreateFormModalOpen}
            onClose={handleOnCloseCreateFormModal}
            onSelected={handleOnSelected}
          />
        )}
      </StyledFileUploadRequest>
    );
  },
);
